import React, { useState, useEffect } from 'react';
import { SideBar } from '../../layout';
import * as mui from '@mui/material';
import SearchAppBar from '../../common/Appbar';
import Separator from '../../layout/Separator';
import ResponsiveContainer from '../../layout/Container';
import HomeIcon from '@mui/icons-material/Home';
import PostAddIcon from '@mui/icons-material/PostAdd';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PublishIcon from '@mui/icons-material/Publish';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LogoLoader from '../../common/LogoLoader';
import { motion } from 'framer-motion';
import MDEditor from '@uiw/react-md-editor';
import BlogService from '../../../services/BlogService';
import { useNavigate } from 'react-router-dom';

const BlogManager = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [blogPosts, setBlogPosts] = useState([]);
    const [view, setView] = useState('list'); // 'list', 'edit', 'create'
    const [selectedPost, setSelectedPost] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        summary: '',
        content: '',
        author: '',
        featured_image: '',
        is_published: true
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [confirmDelete, setConfirmDelete] = useState(null);

    useEffect(() => {
        const fetchBlogPosts = async () => {
            try {
                setIsLoading(true);
                const posts = await BlogService.getBlogPosts();
                setBlogPosts(posts);
                setIsLoading(false);
            } catch (error) {
                console.error('Error fetching blog posts:', error);
                setErrorMessage('Failed to load blog posts. Please try again.');
                setIsLoading(false);
            }
        };

        fetchBlogPosts();
    }, []);

    const filteredPosts = blogPosts.filter(post => 
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.summary.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleCreateNew = () => {
        setFormData({
            title: '',
            summary: '',
            content: '',
            author: '',
            featured_image: '',
            is_published: true
        });
        setView('create');
    };

    const handleEditPost = (post) => {
        setSelectedPost(post);
        setFormData({
            title: post.title,
            summary: post.summary,
            content: post.content,
            author: post.author,
            featured_image: post.featured_image,
            is_published: post.is_published
        });
        setView('edit');
    };

    const handleBackToList = () => {
        setView('list');
        setSelectedPost(null);
        setSuccessMessage('');
        setErrorMessage('');
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleContentChange = (value) => {
        setFormData({
            ...formData,
            content: value || ''
        });
    };

    const handlePublishToggle = () => {
        setFormData({
            ...formData,
            is_published: !formData.is_published
        });
    };

    const handleSavePost = async () => {
        try {
            setIsLoading(true);
            
            // Check required fields
            if (!formData.title || !formData.summary || !formData.content || !formData.author) {
                setErrorMessage('Please fill in all required fields.');
                setIsLoading(false);
                return;
            }
            
            let result;
            
            if (view === 'create') {
                // Prepare data for API
                const postData = {
                    ...formData,
                    // Slug will be generated on server
                };
                
                // Call the API to create a new post
                result = await BlogService.createBlogPost(postData);
                
                // Update local state with the new post from the API
                setBlogPosts([result, ...blogPosts]);
                setSuccessMessage('Blog post created successfully!');
            } else {
                // Prepare data for API
                const postData = {
                    ...formData
                };
                
                // Call the API to update the post
                result = await BlogService.updateBlogPost(selectedPost.id, postData);
                
                // Update local state with the updated post
                const updatedPosts = blogPosts.map(post => 
                    post.id === selectedPost.id ? result : post
                );
                
                setBlogPosts(updatedPosts);
                setSuccessMessage('Blog post updated successfully!');
            }
            
            setIsLoading(false);
            
            // Return to the list view after successful save
            setView('list');
        } catch (error) {
            console.error('Error saving blog post:', error);
            setErrorMessage(`Failed to save blog post: ${error.message}`);
            setIsLoading(false);
        }
    };

    const handleDeleteConfirmation = (post) => {
        setConfirmDelete(post);
    };

    const handleDeletePost = async () => {
        if (!confirmDelete) return;
        
        try {
            setIsLoading(true);
            
            // Call the API to delete the post
            await BlogService.deleteBlogPost(confirmDelete.id);
            
            // Remove the post from the local state
            const updatedPosts = blogPosts.filter(post => post.id !== confirmDelete.id);
            setBlogPosts(updatedPosts);
            
            setConfirmDelete(null);
            setSuccessMessage('Blog post deleted successfully!');
            setIsLoading(false);
        } catch (error) {
            console.error('Error deleting blog post:', error);
            setErrorMessage(`Failed to delete blog post: ${error.message}`);
            setIsLoading(false);
        }
    };

    const handlePreviewPost = (post) => {
        // Open the blog post in a new tab
        window.open(`/blog/${post.slug}`, '_blank');
    };

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.05
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                type: "spring",
                stiffness: 100,
                damping: 12
            }
        }
    };

    const mainContent = (
        <div className="min-h-screen bg-gray-50">
            <ResponsiveContainer>
                <mui.Box sx={{ padding: '24px' }}>
                    <mui.Breadcrumbs aria-label="breadcrumb">
                        <mui.Link underline="hover" color="inherit" href="/Dashboard" sx={{ display: 'flex', alignItems: 'center' }}>
                            <HomeIcon sx={{ mr: 0.5, fontSize: '1.1rem' }} />
                            Home
                        </mui.Link>
                        <mui.Link underline="hover" color="inherit" href="/Dashboard">
                            Dashboard
                        </mui.Link>
                        <mui.Typography color="text.primary">Blog Manager</mui.Typography>
                    </mui.Breadcrumbs>

                    <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 3, mt: 2 }}>
                        <PostAddIcon sx={{ fontSize: 28, color: 'primary.main', mr: 2 }} />
                        <mui.Box sx={{ flexGrow: 1 }}>
                            <mui.Typography variant="h5" component="h1" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                                {view === 'list' ? 'Blog Manager' : view === 'create' ? 'Create New Blog Post' : 'Edit Blog Post'}
                            </mui.Typography>
                            <mui.Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                {view === 'list' ? 'Manage your blog posts, create new content, or edit existing articles' : 'Draft your blog post with our rich editor'}
                            </mui.Typography>
                        </mui.Box>
                        {view === 'list' ? (
                            <mui.Button 
                                variant="contained" 
                                startIcon={<PostAddIcon />}
                                onClick={handleCreateNew}
                                sx={{ 
                                    textTransform: 'none',
                                    background: 'linear-gradient(90deg, #4299e1, #667eea)',
                                    boxShadow: '0 4px 6px rgba(66, 153, 225, 0.2)',
                                    '&:hover': {
                                        background: 'linear-gradient(90deg, #3182ce, #5a67d8)',
                                        boxShadow: '0 4px 12px rgba(66, 153, 225, 0.4)'
                                    }
                                }}
                            >
                                Create New Post
                            </mui.Button>
                        ) : (
                            <mui.Button 
                                variant="outlined" 
                                startIcon={<ArrowBackIcon />}
                                onClick={handleBackToList}
                                sx={{ textTransform: 'none' }}
                            >
                                Back to List
                            </mui.Button>
                        )}
                    </mui.Box>

                    <Separator />

                    {/* Success or Error Messages */}
                    {successMessage && (
                        <mui.Alert 
                            severity="success" 
                            sx={{ mt: 2, mb: 2 }}
                            onClose={() => setSuccessMessage('')}
                        >
                            {successMessage}
                        </mui.Alert>
                    )}
                    
                    {errorMessage && (
                        <mui.Alert 
                            severity="error" 
                            sx={{ mt: 2, mb: 2 }}
                            onClose={() => setErrorMessage('')}
                        >
                            {errorMessage}
                        </mui.Alert>
                    )}

                    {/* Delete Confirmation Dialog */}
                    <mui.Dialog
                        open={!!confirmDelete}
                        onClose={() => setConfirmDelete(null)}
                    >
                        <mui.DialogTitle>Confirm Delete</mui.DialogTitle>
                        <mui.DialogContent>
                            <mui.DialogContentText>
                                Are you sure you want to delete the blog post "{confirmDelete?.title}"? This action cannot be undone.
                            </mui.DialogContentText>
                        </mui.DialogContent>
                        <mui.DialogActions>
                            <mui.Button onClick={() => setConfirmDelete(null)}>Cancel</mui.Button>
                            <mui.Button onClick={handleDeletePost} color="error" autoFocus>
                                Delete
                            </mui.Button>
                        </mui.DialogActions>
                    </mui.Dialog>

                    {/* List View */}
                    {view === 'list' && (
                        <mui.Box sx={{ mt: 3 }}>
                            {/* Search Bar */}
                            <mui.TextField
                                fullWidth
                                variant="outlined"
                                placeholder="Search blog posts..."
                                value={searchTerm}
                                onChange={handleSearchChange}
                                InputProps={{
                                    startAdornment: (
                                        <mui.InputAdornment position="start">
                                            <mui.Icon>search</mui.Icon>
                                        </mui.InputAdornment>
                                    ),
                                }}
                                sx={{ mb: 3 }}
                            />

                            {/* Blog Posts List */}
                            <motion.div
                                variants={containerVariants}
                                initial="hidden"
                                animate="visible"
                            >
                                {filteredPosts.length === 0 ? (
                                    <mui.Paper sx={{ p: 4, textAlign: 'center' }}>
                                        <mui.Typography variant="h6">No blog posts found</mui.Typography>
                                        <mui.Typography variant="body2" color="textSecondary">
                                            {searchTerm ? 'Try adjusting your search terms' : 'Create your first blog post to get started'}
                                        </mui.Typography>
                                    </mui.Paper>
                                ) : (
                                    filteredPosts.map((post) => (
                                        <motion.div key={post.id} variants={itemVariants}>
                                            <mui.Card 
                                                sx={{ 
                                                    mb: 2, 
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                                    transition: 'all 0.2s ease-in-out',
                                                    '&:hover': {
                                                        boxShadow: '0 5px 15px rgba(0,0,0,0.08)'
                                                    }
                                                }}
                                            >
                                                <mui.CardContent>
                                                    <mui.Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                                        <mui.Box sx={{ flexGrow: 1 }}>
                                                            <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                                <mui.Typography variant="h6" component="div">
                                                                    {post.title}
                                                                </mui.Typography>
                                                                {post.is_published ? (
                                                                    <mui.Chip 
                                                                        size="small" 
                                                                        label="Published" 
                                                                        color="success" 
                                                                        sx={{ ml: 2 }}
                                                                    />
                                                                ) : (
                                                                    <mui.Chip 
                                                                        size="small" 
                                                                        label="Draft" 
                                                                        color="default" 
                                                                        sx={{ ml: 2 }}
                                                                    />
                                                                )}
                                                            </mui.Box>
                                                            <mui.Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                                                By {post.author} • {new Date(post.published_date).toLocaleDateString()}
                                                            </mui.Typography>
                                                            <mui.Typography variant="body2">
                                                                {post.summary.length > 150 
                                                                    ? `${post.summary.substring(0, 150)}...` 
                                                                    : post.summary}
                                                            </mui.Typography>
                                                        </mui.Box>
                                                        <mui.Box>
                                                            <mui.IconButton 
                                                                color="info" 
                                                                aria-label="view"
                                                                onClick={() => handlePreviewPost(post)}
                                                            >
                                                                <VisibilityIcon />
                                                            </mui.IconButton>
                                                            <mui.IconButton 
                                                                color="primary" 
                                                                aria-label="edit"
                                                                onClick={() => handleEditPost(post)}
                                                            >
                                                                <EditIcon />
                                                            </mui.IconButton>
                                                            <mui.IconButton 
                                                                color="error" 
                                                                aria-label="delete"
                                                                onClick={() => handleDeleteConfirmation(post)}
                                                            >
                                                                <DeleteIcon />
                                                            </mui.IconButton>
                                                        </mui.Box>
                                                    </mui.Box>
                                                </mui.CardContent>
                                            </mui.Card>
                                        </motion.div>
                                    ))
                                )}
                            </motion.div>
                        </mui.Box>
                    )}

                    {/* Edit or Create View */}
                    {(view === 'edit' || view === 'create') && (
                        <mui.Box sx={{ mt: 3 }}>
                            <mui.Paper sx={{ p: 3 }}>
                                <mui.Grid container spacing={3}>
                                    <mui.Grid item xs={12}>
                                        <mui.TextField
                                            fullWidth
                                            label="Blog Title"
                                            name="title"
                                            value={formData.title}
                                            onChange={handleInputChange}
                                            required
                                            variant="outlined"
                                        />
                                    </mui.Grid>
                                    
                                    <mui.Grid item xs={12} sm={6}>
                                        <mui.TextField
                                            fullWidth
                                            label="Author"
                                            name="author"
                                            value={formData.author}
                                            onChange={handleInputChange}
                                            required
                                            variant="outlined"
                                        />
                                    </mui.Grid>
                                    
                                    <mui.Grid item xs={12} sm={6}>
                                        <mui.TextField
                                            fullWidth
                                            label="Featured Image URL"
                                            name="featured_image"
                                            value={formData.featured_image}
                                            onChange={handleInputChange}
                                            variant="outlined"
                                            placeholder="https://example.com/image.jpg"
                                        />
                                    </mui.Grid>
                                    
                                    <mui.Grid item xs={12}>
                                        <mui.TextField
                                            fullWidth
                                            label="Summary"
                                            name="summary"
                                            value={formData.summary}
                                            onChange={handleInputChange}
                                            required
                                            variant="outlined"
                                            multiline
                                            rows={3}
                                        />
                                    </mui.Grid>
                                    
                                    <mui.Grid item xs={12}>
                                        <mui.Typography variant="subtitle1" gutterBottom>
                                            Content (Markdown)
                                        </mui.Typography>
                                        <div data-color-mode="light">
                                            <MDEditor
                                                value={formData.content}
                                                onChange={handleContentChange}
                                                height={400}
                                                preview="edit"
                                            />
                                        </div>
                                    </mui.Grid>
                                    
                                    <mui.Grid item xs={12}>
                                        <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                                            <mui.FormControlLabel
                                                control={
                                                    <mui.Switch
                                                        checked={formData.is_published}
                                                        onChange={handlePublishToggle}
                                                        color="primary"
                                                    />
                                                }
                                                label={formData.is_published ? "Published" : "Draft"}
                                            />
                                            
                                            <mui.Box>
                                                <mui.Button
                                                    variant="outlined"
                                                    onClick={handleBackToList}
                                                    sx={{ mr: 2 }}
                                                >
                                                    Cancel
                                                </mui.Button>
                                                <mui.Button
                                                    variant="contained"
                                                    onClick={handleSavePost}
                                                    startIcon={formData.is_published ? <PublishIcon /> : <UnpublishedIcon />}
                                                    sx={{ 
                                                        textTransform: 'none',
                                                        background: 'linear-gradient(90deg, #4299e1, #667eea)',
                                                        boxShadow: '0 4px 6px rgba(66, 153, 225, 0.2)',
                                                        '&:hover': {
                                                            background: 'linear-gradient(90deg, #3182ce, #5a67d8)',
                                                            boxShadow: '0 4px 12px rgba(66, 153, 225, 0.4)'
                                                        }
                                                    }}
                                                >
                                                    {view === 'create' 
                                                        ? `Save as ${formData.is_published ? 'Published' : 'Draft'}` 
                                                        : `Update ${formData.is_published ? 'Published Post' : 'Draft'}`}
                                                </mui.Button>
                                            </mui.Box>
                                        </mui.Box>
                                    </mui.Grid>
                                </mui.Grid>
                            </mui.Paper>
                        </mui.Box>
                    )}
                </mui.Box>
            </ResponsiveContainer>
        </div>
    );

    return (
        <div>
            {isLoading && view !== 'list' ? (
                <mui.Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                    <LogoLoader 
                        size={80} 
                        message="Loading..." 
                        duration={1.5} 
                    />
                </mui.Box>
            ) : (
                <SideBar mainContent={mainContent} />
            )}
        </div>
    );
};

export default BlogManager; 